import { FC } from "react";
import { FieldInputProps } from "react-final-form";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { MultiOptionInput } from "components/multi-option-input/MultiOptionInput";
import { isEmail } from "helpers/validate";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { IRuleTriggerRequesterEmail } from "store/entities/rules/interfaces";

import { changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerRequesterEmail>;
}

type EqualityType = "contains" | "doesNotContain" | "isOneOf" | "isNotOneOf";

export const RequesterTrigger: FC<IProps> = ({ input }) => {
  const equalityOptions = [
    createEqualityOption("isOneOf"),
    createEqualityOption("contains"),
    createEqualityOption("isNotOneOf"),
    createEqualityOption("doesNotContain"),
  ];

  const selectedEqualityOption = [createEqualityOption(getEqualityOption(input.value))];

  const selectedPhrases = input.value.in || input.value.containsIn || [];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];

    switch (selectedOption.key) {
      case "contains":
        changeTriggerValues(input, { containsIn: selectedPhrases, invert: false }, { overwrite: true });
        break;
      case "doesNotContain":
        changeTriggerValues(input, { containsIn: selectedPhrases, invert: true }, { overwrite: true });
        break;
      case "isOneOf":
        changeTriggerValues(input, { in: selectedPhrases, invert: false }, { overwrite: true });
        break;
      case "isNotOneOf":
        changeTriggerValues(input, { in: selectedPhrases, invert: true }, { overwrite: true });
        break;
    }
  };

  const handleAddPhrase = (phrase: string): void => {
    const currentKey = getEqualityOption(input.value);
    const newPhrases = [...selectedPhrases, phrase];

    switch (currentKey) {
      case "contains":
        changeTriggerValues(input, { containsIn: newPhrases }, { overwrite: true });
        break;
      case "doesNotContain":
        changeTriggerValues(input, { containsIn: newPhrases, invert: true }, { overwrite: true });
        break;
      case "isOneOf":
        changeTriggerValues(input, { in: newPhrases }, { overwrite: true });
        break;
      case "isNotOneOf":
        changeTriggerValues(input, { in: newPhrases, invert: true }, { overwrite: true });
        break;
    }
  };

  const handleRemovePhrase = (phrase: string): void => {
    const currentKey = getEqualityOption(input.value);
    const newPhrases = selectedPhrases.filter(p => p !== phrase);

    switch (currentKey) {
      case "contains":
        changeTriggerValues(input, { containsIn: newPhrases }, { overwrite: true });
        break;
      case "doesNotContain":
        changeTriggerValues(input, { containsIn: newPhrases, invert: true }, { overwrite: true });
        break;
      case "isOneOf":
        changeTriggerValues(input, { in: newPhrases }, { overwrite: true });
        break;
      case "isNotOneOf":
        changeTriggerValues(input, { in: newPhrases, invert: true }, { overwrite: true });
        break;
    }
  };

  const validateInput = (phrase: string): string | null => {
    const mustEnterEmail = ["isOneOf", "isNotOneOf"].includes(getEqualityOption(input.value));

    if (mustEnterEmail && !isEmail(phrase) && phrase.trim().length > 0) {
      return "Enter correct email";
    }

    if (selectedPhrases.includes(phrase)) {
      return mustEnterEmail ? "Email already entered" : "Part of email already entered";
    }

    return null;
  };

  const validateOption = (phrase: string): string | null => {
    const mustEnterEmail = ["isOneOf", "isNotOneOf"].includes(getEqualityOption(input.value));

    if (mustEnterEmail && !isEmail(phrase)) {
      return "Enter correct email";
    }

    return null;
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOption}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <MultiOptionInput
        placeholder={
          ["isOneOf", "isNotOneOf"].includes(getEqualityOption(input.value))
            ? "Enter email and confirm it"
            : "Enter part of email and confirm it"
        }
        selectedOptions={selectedPhrases}
        onOptionRemove={handleRemovePhrase}
        onOptionAdd={handleAddPhrase}
        validateInput={validateInput}
        validateOption={validateOption}
      />
    </>
  );
};

const createEqualityOption = (key: EqualityType): IPickerListItem => {
  switch (key) {
    case "contains":
      return { key: "contains", name: "contains" };
    case "doesNotContain":
      return { key: "doesNotContain", name: "does not contain" };
    case "isOneOf":
      return { key: "isOneOf", name: "is one of" };
    case "isNotOneOf":
      return { key: "isNotOneOf", name: "is not one of" };
  }
};

const getEqualityOption = (value: IRuleTriggerRequesterEmail): EqualityType => {
  if (value.in) {
    return value.invert ? "isNotOneOf" : "isOneOf";
  }

  return value.invert ? "doesNotContain" : "contains";
};
